/**
 * Sistema de Animações Centralizado
 * Gerencia animações de surgimento baseadas em Intersection Observer
 */

(function() {
  'use strict';

  // Verificar se o usuário prefere movimento reduzido
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /**
   * Inicializa animação para um elemento específico
   * @param {string} selector - Seletor CSS do elemento
   * @param {Object} options - Opções de configuração
   * @param {Function} [options.onVisible] - Callback quando elemento fica visível
   * @param {string} [options.animatedClass] - Classe que indica que o elemento está animado
   */
  function initAnimation(selector, options = {}) {
    const element = document.querySelector(selector);
    const animatedClass = options.animatedClass || '--animated';
    
    if (!element || !element.className.includes(animatedClass)) {
      return;
    }

    // Se usuário prefere movimento reduzido, mostrar imediatamente
    if (prefersReducedMotion) {
      element.classList.add('is-visible');
      if (options.onVisible) {
        options.onVisible(element);
      }
      return;
    }

    // Função para verificar se está visível e animar
    function checkAndAnimate() {
      const rect = element.getBoundingClientRect();
      const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
      
      if (isVisible) {
        // Se já está visível, animar imediatamente
        element.classList.add('is-visible');
        if (options.onVisible) {
          options.onVisible(element);
        }
        return true;
      }
      return false;
    }

    // Verificar imediatamente
    if (checkAndAnimate()) {
      return;
    }

    // Intersection Observer para detectar quando o elemento está visível
    const observerOptions = {
      root: null,
      rootMargin: '50px',
      threshold: 0.01
    };

    const observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          if (options.onVisible) {
            options.onVisible(entry.target);
          }
          observer.unobserve(entry.target);
        }
      });
    }, observerOptions);

    observer.observe(element);
  }

  /**
   * Inicializa animação de divider
   * @param {string} selector - Seletor CSS do divider line
   */
  function initDividerAnimation(selector) {
    initAnimation(selector, {
      animatedClass: 'divider__line--animated'
    });
  }

  /**
   * Inicializa animação de typewriter
   * @param {string} selector - Seletor CSS do elemento
   * @param {Object} options - Opções do typewriter
   * @param {number} options.speed - Velocidade em ms entre cada letra
   * @param {number} options.delay - Delay antes de iniciar
   */
  function initTypewriter(selector, options = {}) {
    const element = document.querySelector(selector);
    if (!element) return;

    const speed = options.speed || 30;
    const delay = options.delay || 0;
    const originalText = element.textContent || element.innerText;
    const originalHTML = element.innerHTML;

    // Limpar o conteúdo
    element.innerHTML = '';
    element.style.opacity = '1';

    // Adicionar letras uma por uma
    let currentIndex = 0;
    
    function addNextChar() {
      if (currentIndex >= originalText.length) {
        // Restaurar HTML original
        element.innerHTML = originalHTML;
        element.classList.add('typewriter-complete');
        return;
      }

      const char = originalText[currentIndex];
      const textNode = document.createTextNode(char);
      element.appendChild(textNode);

      currentIndex++;
      setTimeout(addNextChar, speed);
    }

    // Iniciar após o delay
    setTimeout(addNextChar, delay);
  }

  /**
   * Inicializa múltiplas animações de typewriter em uma lista
   * @param {string} selector - Seletor CSS base (deve retornar múltiplos elementos)
   * @param {Object} options - Opções do typewriter
   */
  function initTypewriterList(selector, options = {}) {
    const elements = document.querySelectorAll(selector);
    if (!elements.length) return;

    const speed = options.speed || 30;
    const delay = options.delay || 0;
    const staggerDelay = options.staggerDelay || 200; // Delay entre cada elemento

    elements.forEach((element, index) => {
      const originalText = element.getAttribute('data-original-text') || element.textContent || element.innerText;
      const originalHTML = element.getAttribute('data-original-html') || element.innerHTML;

      // Limpar o conteúdo
      element.innerHTML = '';
      element.style.opacity = '1';

      // Adicionar letras uma por uma
      let currentIndex = 0;
      
      function addNextChar() {
        if (currentIndex >= originalText.length) {
          // Restaurar HTML original
          element.innerHTML = originalHTML;
          element.classList.add('typewriter-complete');
          return;
        }

        const char = originalText[currentIndex];
        const textNode = document.createTextNode(char);
        element.appendChild(textNode);

        currentIndex++;
        setTimeout(addNextChar, speed);
      }

      // Iniciar após o delay + stagger
      setTimeout(addNextChar, delay + (index * staggerDelay));
    });
  }

  /**
   * Inicializa animações para todos os elementos animados em um container
   * Usado quando conteúdo é inserido dinamicamente (ex: filtros, morph)
   * @param {HTMLElement} container - Container onde buscar elementos
   */
  function initInContainer(container) {
    if (!container) return;

    const selector = '[class*="--animated"]:not(.is-visible)';
    const elements = container.querySelectorAll(selector);
    if (elements.length === 0) return;

    elements.forEach(function(element) {
      if (prefersReducedMotion) {
        element.classList.add('is-visible');
        return;
      }

      function checkAndAnimate() {
        const rect = element.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
        if (isVisible) {
          element.classList.add('is-visible');
          return true;
        }
        return false;
      }

      if (checkAndAnimate()) return;

      const observerOptions = {
        root: null,
        rootMargin: '50px',
        threshold: 0.01
      };

      const observer = new IntersectionObserver(function(entries) {
        entries.forEach(function(entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      }, observerOptions);

      observer.observe(element);
    });
  }

  /** @type {WeakMap<HTMLElement, { io?: IntersectionObserver, mo?: MutationObserver, parentMo?: MutationObserver }>} */
  const cascadeWatchers = new WeakMap();

  /**
   * Filhos elegíveis para cascata (ignora título virtual e blocos com animação individual).
   * @param {HTMLElement} container
   * @returns {HTMLElement[]}
   */
  function getCascadeChildren(container) {
    const children = [];

    for (const child of container.children) {
      if (!(child instanceof HTMLElement)) continue;
      if (child.classList.contains('view-product-title')) continue;

      let hasIndividualAnimation = false;
      for (const className of child.classList) {
        if (className.includes('--animated')) {
          hasIndividualAnimation = true;
          break;
        }
      }
      if (hasIndividualAnimation) continue;

      children.push(child);
    }

    return children;
  }

  /**
   * @param {HTMLElement} container
   * @returns {boolean}
   */
  function isVisibilityParentReady(container) {
    const parentSelector = container.dataset.cascadeVisibilityParent;
    if (!parentSelector) return true;

    const parent = container.closest(parentSelector);
    if (!parent) return true;

    if (parent.hasAttribute('hidden')) return false;
    return parent.getAttribute('aria-hidden') !== 'true';
  }

  /**
   * @param {HTMLElement} container
   * @param {boolean} [force]
   */
  function triggerCascade(container, force) {
    if (!force && container.dataset.cascadeTriggered === 'true') return;
    if (!isVisibilityParentReady(container)) return;

    const children = getCascadeChildren(container);
    if (children.length === 0) return;

    const initialDelay = parseInt(container.dataset.cascadeInitialDelay, 10) || 0;
    const stagger = parseInt(container.dataset.cascadeStagger, 10) || 250;

    if (prefersReducedMotion) {
      for (const child of children) {
        child.style.removeProperty('--cascade-item-delay');
        child.classList.add('cascade-visible');
      }
      container.dataset.cascadeTriggered = 'true';
      container.cascadeInitialized = true;
      return;
    }

    children.forEach(function(child, index) {
      const delay = initialDelay + index * stagger;
      child.style.setProperty('--cascade-item-delay', `${delay}ms`);
      child.classList.add('cascade-visible');
    });

    container.dataset.cascadeTriggered = 'true';
    container.cascadeInitialized = true;
  }

  /**
   * @param {HTMLElement} container
   */
  function resetCascade(container) {
    const watchers = cascadeWatchers.get(container);
    if (watchers) {
      if (watchers.io) watchers.io.disconnect();
      if (watchers.mo) watchers.mo.disconnect();
      if (watchers.parentMo) watchers.parentMo.disconnect();
      cascadeWatchers.delete(container);
    }

    delete container.cascadeInitialized;
    delete container.dataset.cascadeTriggered;

    for (const child of getCascadeChildren(container)) {
      child.classList.remove('cascade-visible');
      child.style.removeProperty('--cascade-item-delay');
    }
  }

  /**
   * Inicializa cascata em um container com data-cascade-enabled.
   * @param {HTMLElement} container
   */
  function initCascade(container) {
    if (!container || container.dataset.cascadeEnabled !== 'true') return;

    if (container.cascadeInitialized === true) return;

    const existing = cascadeWatchers.get(container);
    if (existing && container.dataset.cascadeTriggered !== 'true') {
      triggerCascade(container, false);
      return;
    }

    const attemptTrigger = function() {
      triggerCascade(container, false);
    };

    if (prefersReducedMotion) {
      attemptTrigger();
      return;
    }

    const io = new IntersectionObserver(
      function(entries) {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            attemptTrigger();
            if (container.dataset.cascadeTriggered === 'true') {
              io.unobserve(container);
            }
          }
        }
      },
      { root: null, rootMargin: '100px', threshold: 0 }
    );

    const mo = new MutationObserver(function() {
      if (container.dataset.cascadeTriggered === 'true') return;
      attemptTrigger();
    });

    mo.observe(container, { childList: true });

    let parentMo;
    const parentSelector = container.dataset.cascadeVisibilityParent;
    if (parentSelector) {
      const parent = container.closest(parentSelector);
      if (parent) {
        parentMo = new MutationObserver(function() {
          if (!isVisibilityParentReady(container)) return;
          attemptTrigger();
        });
        parentMo.observe(parent, {
          attributes: true,
          attributeFilter: ['aria-hidden', 'hidden']
        });
      }
    }

    cascadeWatchers.set(container, { io, mo, parentMo });
    io.observe(container);
    attemptTrigger();
  }

  /**
   * Inicializa todas as cascatas no documento (ou escopo).
   * @param {ParentNode} [scope]
   */
  function initAllCascades(scope) {
    const root = scope || document;
    const containers = root.querySelectorAll('[data-cascade-enabled="true"]');

    for (const container of containers) {
      if (container instanceof HTMLElement) {
        initCascade(container);
      }
    }
  }

  /**
   * Retry após layout completo (storefront pode falhar no primeiro bootstrap).
   */
  function retryUninitializedCascades() {
    let attempts = 0;
    const maxAttempts = 30;

    const tick = function() {
      attempts += 1;
      const containers = document.querySelectorAll(
        '[data-cascade-enabled="true"]:not([data-cascade-triggered="true"])'
      );

      for (const container of containers) {
        if (container instanceof HTMLElement) {
          initCascade(container);
          triggerCascade(container, false);
        }
      }

      if (attempts < maxAttempts && containers.length > 0) {
        setTimeout(tick, 50);
      }
    };

    tick();
  }

  function bootstrapCascades() {
    initAllCascades();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrapCascades);
  } else {
    bootstrapCascades();
  }

  window.addEventListener('load', retryUninitializedCascades);
  window.addEventListener('pagereveal', bootstrapCascades);

  document.addEventListener('shopify:section:load', function(event) {
    const section = event.target;
    if (!(section instanceof HTMLElement)) {
      initAllCascades();
      return;
    }

    const containers = section.querySelectorAll('[data-cascade-enabled="true"]');
    for (const container of containers) {
      if (container instanceof HTMLElement) {
        resetCascade(container);
        initCascade(container);
      }
    }
  });

  // Exportar funções para uso global
  window.AnimationSystem = {
    init: initAnimation,
    initInContainer: initInContainer,
    initDivider: initDividerAnimation,
    initTypewriter: initTypewriter,
    initTypewriterList: initTypewriterList,
    initCascade: initCascade,
    initAllCascades: initAllCascades,
    resetCascade: resetCascade
  };

})();

